"use client";

import { MapPinIcon } from "@heroicons/react/24/outline";
import { searchLocations } from "api/locations/locations";
import { useData } from "data/data-provider";
import { FC, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import ClearDataButton from "./ClearDataButton";
import LocationModal from "./LocationModal";

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  sameDropOffLocation?: boolean;
  searchBar?: boolean;
  onShowDropoffLocation?: () => void;
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "Location",
  desc = "Where are you going?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  sameDropOffLocation,
  searchBar,
  onShowDropoffLocation,
}) => {
  const {
    ourLocation,
    setPickupLocation,
    searchedPickupLoction,
    setSearchedPickupLocation,
    pickupLocation,
    ourCompany,
    setDropoffLocationType,
    setSameDropoffLocation,
    setDropoffLocation,
    setSearchedDropoffLocation,
  } = useData();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchedLocations, setSearchedLocations] = useState<any>();
  const [showPopover, setShowPopover] = useState(autoFocus);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const callSearchLocationsAPI = async (input: string) => {
    try {
      const data = (await searchLocations(input)) as any;
      setSearchedLocations(data.predictions);
    } catch (error: any) {
      toast.error(error.message); // Or handle errors in a more sophisticated way
    }
  };

  useEffect(() => {
    callSearchLocationsAPI(searchedPickupLoction);
  }, [searchedPickupLoction]);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  useEffect(() => {
    if (pickupLocation === null) {
      setSearchedPickupLocation(""); // Clear state
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field display
      }
    }
  }, [pickupLocation, setSearchedPickupLocation]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    setShowPopover(false);
  };

  const handleSelectLocation = (item: any) => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    if (item.radius) {
      setSelectedLocation(item);
      setShowLocationModal(true);
      setShowPopover(false);
    } else {
      setPickupLocation(item);
      setShowPopover(false);
    }
  };

  const handleClear = () => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    setShowPopover(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const renderRecentSearches = () => {
    return (
      <div className="rounded-2xl border border-secondary-100 bg-bg-color shadow-2xl shadow-text-color/10 ring-1 ring-text-color/5 overflow-hidden p-1.5">
        {ourLocation
          .filter((item) => item.active !== false)
          .sort((a, b) => a.price - b.price)
          .map((item: any) => (
            <div
              key={item.id}
              onClick={() => handleSelectLocation(item)}
              className="group/loc flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 cursor-pointer hover:bg-secondary-50 rounded-xl transition-colors text-text-color"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100/15 ring-1 ring-primary-200/30 transition-colors group-hover/loc:bg-primary-100/30 group-hover/loc:ring-primary-200/60">
                  <MapPinIcon className="h-4 w-4 text-primary-300" />
                </span>
                <span className="truncate text-base font-semibold">{item.name}</span>
              </div>
              <span
                className="shrink-0 inline-flex items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-text-color/80"
                style={{ minWidth: 72, justifyContent: "center" }}
              >
                {item.price === 0 ? "Free" : `$${item.price}/trip`}
              </span>
            </div>
          ))}
        {ourCompany?.allow_long_term_rentals &&
          ourLocation.some(
            (loc) =>
              loc.active !== false &&
              (loc.location_type === "drop_off" || loc.location_type === "both")
          ) && (
            <div className="border-t border-secondary-100 mt-1.5 pt-1.5 flex">
              <button
                className="w-full text-left rounded-xl px-4 sm:px-5 py-3 text-sm font-semibold text-primary-300 hover:bg-secondary-50 transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  onShowDropoffLocation && onShowDropoffLocation();
                  setShowPopover(false);
                  setDropoffLocationType(false);
                  setSameDropoffLocation(false);
                  const activeLocations = ourLocation.filter((loc) => loc.active !== false);
                  if (activeLocations.length === 1) {
                    setPickupLocation(activeLocations[0]);
                    setDropoffLocation(activeLocations[0]);
                    setSearchedPickupLocation(activeLocations[0].name ?? "");
                    setSearchedDropoffLocation(activeLocations[0].name ?? "");
                  }
                }}
              >
                + Different dropoff location
              </button>
            </div>
          )}
      </div>
    );
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ]  flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${showPopover ? "nc-hero-field-focused !rounded-md" : ""
          }`}
      >
        <div className="flex-grow">
          <p className="text-text-color font-semibold">{placeHolder}</p>
          <div className="relative w-full flex items-center">
            <input
              className="block w-full text-text-color pr-10 bg-transparent cursor-pointer border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-text-color xl:text-lg font-semibold placeholder-text-color truncate"
              placeholder={desc}
              value={
                searchedPickupLoction?.length > 0
                  ? searchedPickupLoction
                  : pickupLocation?.name
              }
              autoFocus={showPopover}
              onChange={(e) => {
                setSearchedPickupLocation(e.currentTarget.value);
              }}
              ref={inputRef}
              readOnly
            />
            {(searchedPickupLoction || pickupLocation) &&
              ourLocation.filter((loc) => loc.active !== false).length > 1 && (
                <ClearDataButton onClick={handleClear} />
              )}
          </div>
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0  ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover &&
        ((searchedPickupLoction && searchedLocations?.length > 0) ||
          ourLocation.filter((item) => item.active !== false).length > 0) && (
          <div className="absolute overflow-y-auto left-0 z-40 w-full rounded-lg sm:min-w-[500px] top-full mt-3 max-h-96">
            {renderRecentSearches()}
          </div>
        )}
      <LocationModal
        isOpen={showLocationModal}
        selectedLocation={selectedLocation}
        onClose={() => setShowLocationModal(false)}
        ourCompany={ourCompany}
        setSearchedPickupLocation={setSearchedPickupLocation}
        setPickupLocation={setPickupLocation}
        ourLocation={ourLocation}
      />
    </div>
  );
};

export default LocationInput;
